/**
 * AdvisorWorkflowImpact Component
 * 
 * Shows how advisor decisions affect shop workflow (midday waiters, late auths, etc.)
 */

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Waves, Clock, Calendar, Package } from 'lucide-react';
import type { AdvisorIntelligenceStats } from '@/hooks/useAdvisorIntelligenceStats';

interface AdvisorWorkflowImpactProps {
  stats: AdvisorIntelligenceStats | null | undefined;
  isSelfView: boolean;
}

export function AdvisorWorkflowImpact({ stats, isSelfView }: AdvisorWorkflowImpactProps) {
  if (!stats) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Waves className="h-5 w-5" />
            Workflow Impact
          </CardTitle>
          <CardDescription>Not enough data to analyze workflow patterns yet.</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  const formatRate = (rate: number | null) => {
    if (rate == null) return '—';
    return `${Math.round(rate * 100)}%`;
  };

  // Calculate overall impact score (lower is better for these metrics)
  const impactMetrics = [
    stats.middayWaiterRate,
    stats.lateAuthRate,
    stats.partsDelayRate,
  ].filter((m): m is number => m != null);
  
  const avgImpact = impactMetrics.length > 0 
    ? impactMetrics.reduce((a, b) => a + b, 0) / impactMetrics.length 
    : null;

  const getImpactLevel = (impact: number | null) => {
    if (impact == null) return { label: 'Unknown', color: 'text-muted-foreground' };
    if (impact < 0.1) return { label: 'Minimal', color: 'text-green-600 dark:text-green-400' };
    if (impact < 0.25) return { label: 'Moderate', color: 'text-amber-600 dark:text-amber-400' };
    return { label: 'Significant', color: 'text-red-600 dark:text-red-400' };
  };

  const impactLevel = getImpactLevel(avgImpact);

  const metrics = [
    {
      label: 'Midday Waiter Rate',
      value: formatRate(stats.middayWaiterRate),
      icon: Clock,
      description: 'Waiters added during peak hours (11am-2pm)',
      rate: stats.middayWaiterRate,
    },
    {
      label: 'Late Authorization Rate',
      value: formatRate(stats.lateAuthRate),
      icon: Calendar,
      description: 'Authorizations after 3pm that may push work to next day',
      rate: stats.lateAuthRate,
    },
    {
      label: 'Parts Delay Rate',
      value: formatRate(stats.partsDelayRate),
      icon: Package,
      description: 'Jobs delayed due to parts not identified at write-up',
      rate: stats.partsDelayRate,
    },
  ];

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-lg flex items-center gap-2">
              <Waves className="h-5 w-5" />
              Workflow Impact
            </CardTitle>
            <CardDescription>
              How decisions ripple through the shop's daily schedule
            </CardDescription>
          </div>
          
          {!isSelfView && avgImpact != null && (
            <div className="text-right">
              <div className="text-xs text-muted-foreground">Overall Impact</div>
              <div className={`text-lg font-semibold ${impactLevel.color}`}>
                {impactLevel.label}
              </div>
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {metrics.map((metric) => (
            <div key={metric.label} className="space-y-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-sm">
                  <metric.icon className="h-4 w-4 text-muted-foreground" />
                  <span className="font-medium">{metric.label}</span>
                </div>
                <span className="font-semibold">{metric.value}</span>
              </div>
              {metric.rate != null && (
                <Progress 
                  value={(1 - metric.rate) * 100} 
                  className="h-2"
                />
              )}
              <p className="text-xs text-muted-foreground">{metric.description}</p>
            </div>
          ))}
        </div>

        {/* Guidance for self-view */}
        {isSelfView && (
          <div className="mt-4 p-3 bg-muted/30 rounded-lg">
            <p className="text-sm text-muted-foreground">
              💡 <strong>Tip:</strong> Scheduling waiters outside peak hours and authorizing 
              work earlier in the day helps technicians stay on track.
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
